import { createElement } from "react";
import { toast } from "sonner";
import { TOAST_DURATION } from "@/shared/utils/toast";
import type { QueuedAction } from "@/v2/hooks/useActionQueue";
import { ProgressToast, PROGRESS_TOAST_OPTIONS } from "@/v2/components/ui/progress-toast";
import { plural, type ActionDeps } from "./types";

export type ExportResult = {
	exportedIds: number[];
	failedEntries: { id: number; error: string }[];
};

function showProgressToast(
	id: string,
	message: string,
	progress: number,
	weighted = true,
) {
	toast.custom(
		() => createElement(ProgressToast, { message, progress, weighted }),
		{ id, ...PROGRESS_TOAST_OPTIONS },
	);
}

export function exportAction(
	ids: number[],
	deps: ActionDeps,
	onComplete?: (result: ExportResult) => void,
	onEntryFailure?: (id: number, error: string) => void,
): QueuedAction {
	const n = ids.length;
	const actionId = crypto.randomUUID();

	return {
		id: actionId,
		execute: () => executeExport(actionId, ids, deps, onComplete, onEntryFailure),
		rollback: null,
		toast: {
			// Single entry: plain loading toast (no progress bar needed)
			// Multiple entries: custom ProgressToast with progress bar
			loading: n === 1
				? `Exporting entry…`
				: () =>
					createElement(ProgressToast, {
						message: `Exporting ${n} ${plural(n)}…`,
						progress: 0,
						weighted: true,
					}),
			success: `Exported ${n} ${plural(n)}`,
			error: "Failed to export entries",
		},
		onSuccess: () => deps.refreshRecords(),
		onError: () => deps.refreshRecords(),
	};
}

export async function executeExport(
	actionId: string,
	ids: number[],
	deps: ActionDeps,
	onComplete?: (result: ExportResult) => void,
	onEntryFailure?: (id: number, error: string) => void,
): Promise<void> {
	const failed: { id: number; error: string }[] = [];
	const exported: number[] = [];
	const total = ids.length;

	for (let i = 0; i < total; i++) {
		const id = ids[i];
		try {
			const resp = await deps.makeApiRequest({
				endpoint: "/records/exportRecords",
				method: "POST",
				body: { ids: [id] },
			});
			if (resp.error) throw new Error(String(resp.error));
			exported.push(id);
		} catch (e) {
			const error = e instanceof Error ? e.message : "Export failed";
			failed.push({ id, error });
			onEntryFailure?.(id, error);
		}

		// Update progress for multi-entry exports
		if (total > 1) {
			showProgressToast(
				actionId,
				`Exporting ${i + 1} of ${total}…`,
				Math.round(((i + 1) / total) * 100),
			);
		}
	}

	// Queue will replace this toast with success/error using the same actionId

	onComplete?.({ exportedIds: exported, failedEntries: failed });

	if (failed.length > 0 && exported.length === 0) {
		throw new Error(
			`All ${failed.length} ${plural(failed.length)} failed to export`,
		);
	}

	if (failed.length > 0) {
		toast.error(
			`${failed.length} ${plural(failed.length)} failed to export`,
			{
				duration: TOAST_DURATION.error,
				description: failed.map((f) => f.error).join("; "),
			},
		);
	}
}
